/**
 * Every user-facing string in the product is addressed by one of these keys.
 *
 * The worker never returns prose. It returns a key, and the client looks the
 * key up in `src/app/i18n/{bg,en}.ts`. That is what keeps Bulgarian the
 * primary language without the API having to know which language the caller
 * reads, and it is what stops an exception message reaching a user.
 *
 * Both locale files are typed as `Record<MessageKey, string>`, so adding a key
 * here fails the typecheck until both translations exist.
 */

export const MESSAGE_KEYS = [
  // --- shell -------------------------------------------------------------
  'app.name',
  'app.tagline',
  'app.footer.disclaimer',
  'app.footer.language',
  'app.loading',

  // --- register ----------------------------------------------------------
  'register.title',
  'register.lead',
  'register.email.label',
  'register.email.hint',
  'register.password.label',
  'register.password.hint',
  'register.nickname.label',
  'register.nickname.hint',
  'register.submit',
  'register.submitting',
  'register.sent.title',
  'register.sent.body',
  'register.have_account',

  // --- verify ------------------------------------------------------------
  'verify.success.title',
  'verify.success.body',
  'verify.failed.title',
  'verify.failed.body',
  'verify.resend',
  'verify.resent',

  // --- sign in -----------------------------------------------------------
  'signin.title',
  'signin.email.label',
  'signin.password.label',
  'signin.submit',
  'signin.submitting',
  'signin.forgot',
  'signin.no_account',

  // --- password reset ----------------------------------------------------
  'forgot.title',
  'forgot.lead',
  'forgot.submit',
  'forgot.sent.title',
  'forgot.sent.body',
  'reset.title',
  'reset.lead',
  'reset.password.label',
  'reset.submit',
  'reset.success.title',
  'reset.success.body',

  // --- account -----------------------------------------------------------
  'account.title',
  'account.signed_in_as',
  'account.verified',
  'account.signout',
  'account.nickname.save',
  'account.delete.title',
  'account.delete.warning',
  'account.delete.confirm_label',
  'account.delete.submit',
  'account.delete.done',

  // --- navigation ----------------------------------------------------------
  'nav.label',
  'nav.world',
  'nav.home',
  'nav.series',
  'nav.books',

  // --- the worlds (site front page) -----------------------------------------
  'worlds.lead',
  'worlds.counts',
  'worlds.next.title',
  'worlds.next.body',
  'page.not_found.title',
  'page.not_found.body',
  'world.not_found.title',
  'world.not_found.body',
  'world.all',

  // --- catalogue -----------------------------------------------------------
  // Public pages. Content (titles, synopses, notes) comes from the database;
  // these are only the words around it.
  'home.choose',
  'home.all_books',
  'catalogue.not_loaded.title',
  'catalogue.not_loaded.body',
  'catalogue.error',
  'series.index.title',
  'series.index.lead',
  'series.reading_order',
  'series.kicker',
  'series.stops.one',
  'series.stops.other',
  'books.count.one',
  'books.count.other',
  'outside.meta',
  'series.all',
  'series.not_found.title',
  'series.not_found.body',
  'outside.lead',
  'books.title',
  'book.not_found.title',
  'book.not_found.body',
  'book.year',
  'book.series',
  'book.position',
  'book.path.label',
  'book.path.nearby',
  'book.path.whole',
  'book.publication_order',
  'book.breadcrumb.stop',
  'book.why',
  'book.why.numbered',
  'book.outside',
  'book.buy',
  // Shown on a book's page when it has no retailer links: a state, not data,
  // so it lives here in both languages rather than in the seed.
  'book.availability.none',
  // NOT UNUSED — no page renders it with today's data. It is for when the
  // nightly link check exists (book-data: retailer_links.last_status): a work
  // whose links have all gone dead is neither known to be on sale nor known
  // not to be. Do not delete as unused.
  'book.availability.unknown',
  'edition.paper.bg',
  'edition.ebook.bg',
  // NOT UNUSED — `edition.audio.*` and `edition.other` are reachable only by
  // data that does not exist yet: an audiobook edition, or an edition entered
  // by hand with no medium or in a language other than bg or en. They are the
  // shape of future data. Do not delete as unused.
  'edition.audio.bg',
  'edition.paper.en',
  'edition.ebook.en',
  'edition.audio.en',
  'edition.other',

  // --- errors ------------------------------------------------------------
  // Deliberately blunt and deliberately few. `auth.invalid_credentials` is
  // returned for an unknown address and for a wrong password alike — see the
  // acceptance criteria in the auth-loop skill.
  'error.invalid_email',
  'error.password_too_short',
  'error.password_too_long',
  'error.password_required',
  'error.invalid_credentials',
  'error.email_not_verified',
  'error.email_taken',
  'error.token_invalid',
  'error.token_expired',
  'error.rate_limited',
  'error.turnstile_failed',
  'error.turnstile_missing',
  'error.message_required',
  'error.message_too_long',
  'error.contact_failed',
  'error.note_too_long',
  'error.nickname_invalid',
  'error.review_required',
  'error.review_too_long',
  'error.reviews_suspended',
  'error.reviews_banned',
  'error.publish_no_note',
  'error.publish_too_long',
  'error.publish_conflict',
  'error.reason_required',
  'error.reason_too_long',
  'error.not_moderator',
  'error.verification_mail_failed',

  // --- site pages: terms, privacy, about, contact ------------------------
  'terms.title',
  'privacy.title',
  'about.title',
  'contact.title',
  'content.updated',
  'content.translation',
  'contact.lead',
  'report.link',
  'report.about',
  'contact.name.label',
  'contact.email.label',
  'contact.email.hint',
  'contact.message.label',
  'contact.submit',
  'contact.sending',
  'contact.sent.title',
  'contact.sent.body',
  'contact.sent.body_anonymous',
  'footer.nav',
  'meta.about.title',
  'meta.about.description',
  'error.not_signed_in',
  'error.confirmation_mismatch',
  'error.bad_request',
  'error.unexpected',

  // --- the quiz: /:world/quiz ------------------------------------------
  'nav.quiz',
  'header.signed_in',
  'quiz.kicker',
  'quiz.title',
  'quiz.count',
  'quiz.submit',
  'quiz.enough',
  'quiz.too_few',
  'quiz.result.heading',
  'quiz.result.clear',
  'quiz.result.close',
  'quiz.result.tie',
  'quiz.result.none',
  'quiz.result.based_on',
  'quiz.result.why',
  'quiz.result.and',
  'quiz.result.start',
  'quiz.result.all',
  'quiz.share.title',
  'quiz.share.copy',
  'quiz.share.copied',
  'quiz.share.failed',
  'quiz.share.label',
  // --- the shelf: /:world/books/:slug, the series page, the account ------
  'shelf.title',
  'shelf.signed_out',
  'shelf.load_failed',
  'shelf.status.legend',
  'shelf.status.want',
  'shelf.status.reading',
  'shelf.status.read',
  'shelf.status.none',
  'shelf.owned',
  'shelf.rating.legend',
  'shelf.rating.none',
  'shelf.note.label',
  'shelf.note.hint',
  'shelf.saving',
  'shelf.saved',
  'shelf.undo',
  'shelf.save_text',
  'shelf.saved_text',
  'shelf.saved_rating',
  'shelf.saved_note',
  'shelf.cleared',
  'shelf.publish.create',
  'shelf.publish.create_hint',
  'shelf.publish.append',
  'shelf.publish.append_hint',
  'shelf.publish.save_first',
  'shelf.publish.created',
  'shelf.publish.appended',
  'shelf.publish.kept',
  'series.progress',
  'account.shelf.title',
  'account.reviews.title',
  'account.reviews.empty',
  'account.shelf.empty',
  'account.shelf.want',
  'account.shelf.reading',
  'account.shelf.read',
  'account.shelf.owned',
  'account.shelf.progress',
  'account.shelf.read_mark',
  'account.quiz.title',
  'account.quiz.result',
  'account.quiz.taken',
  'account.quiz.again',
  'quiz.result.saved',

  // --- reviews on a book's page (issue #89) -------------------------------
  'reviews.title',
  'reviews.none',
  'reviews.signin',
  'reviews.write',
  'reviews.edit',
  'reviews.body.label',
  'reviews.body.hint',
  'reviews.spoilers.label',
  'reviews.publish',
  'reviews.cancel',
  'reviews.delete',
  'reviews.suspended',
  'reviews.banned',
  'reviews.anonymous',
  'reviews.yours',
  'reviews.spoilers.show',
  'reviews.report',
  'reviews.report.reason',
  'reviews.report.send',
  'reviews.report.done',
  'reviews.remove',
  'reviews.remove.reason',
  'reviews.remove.hint',
  'reviews.remove.confirm',
  'reviews.remove.done',
  'reviews.remove.done_unnotified',

  // --- page metadata, rendered by the Worker into each page's <head> -----
  'meta.worlds.title',
  'meta.worlds.description',
  'meta.world.title',
  'meta.series_index.title',
  'meta.series.title',
  'meta.books.title',
  'meta.book.title',
  'meta.quiz.title',
  'meta.quiz.description',
  'meta.quiz.result.title',
  'meta.quiz.result.description',
  'meta.terms.title',
  'meta.terms.description',
  'meta.privacy.title',
  'meta.privacy.description',
  'meta.contact.title',
  'meta.contact.description',
  'meta.verify.title',
  'meta.not_found.title',
  'meta.image.alt',
] as const

export type MessageKey = (typeof MESSAGE_KEYS)[number]

const MESSAGE_KEY_SET: ReadonlySet<string> = new Set(MESSAGE_KEYS)

export function isMessageKey(value: string): value is MessageKey {
  return MESSAGE_KEY_SET.has(value)
}
